import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setNotification } from '../../redux/slices/notificationSlice';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogActions,
  FormControlLabel,
  FormControl,
  FormLabel,
  TextareaAutosize,
  IconButton,
  MenuItem,
  Stack,
  Select,
  Tabs,
  Tab,
  TextField,
  Typography,
  Divider
} from "@mui/material";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from 'react-hook-form';
import ConfirmDialog from "../widgets/ConfirmDialog";
import httpClient from "../../utils/httpClient";
import CustomTabPanel from "../widgets/CustomTabPanel";
import Geolocation from '../map/Geolocation';
import { MuiTelInput } from 'mui-tel-input';
import LocalAutocomplete from "../widgets/LocalAutocomplete";
import { createTaskAsync, updateTaskAsync, updateTaskStatusAsync, /*assignPatrolAsync*/ } from "../../redux/slices/tasksSlice";
import { CALL_HANDLER, CONTROL_TOWER } from "../../utils/config";
import AuditGrid from "../grids/auditGrid";
import CaseGrid from "../grids/caseGrid";
import GarageTaskGrid from "../grids/GarageTaskGrid";
import { datetimeFormat } from "../../utils/utils";

// temporary formatting to standardize phone numbers until backend data is available
const normalizePhone = (rawPhone) => {
  const digitsOnly = rawPhone?.replace(/\D/g, '');
  if (digitsOnly?.startsWith('0')) {
    return '+' + digitsOnly.slice(1);
  }
  if (digitsOnly?.startsWith('00')) {
    return '+' + digitsOnly.slice(2);
  }
  return '+' + digitsOnly;
};

const TaskForm = ({ open, onClose, entity, setFormIsDirty }) => {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const dispatch = useDispatch();
  const patrols = useSelector((state) => state.patrols.allPatrols) || [];
  const taskLoading = useSelector((state) => state.tasks.loading);
  const { user, roles } = useSelector((state) => state.auth) || [];
  const [openComplete, setOpenComplete] = useState(false);
  const [tabsValue, setTabsValue] = useState(0);
  const [geolocationForBreakdown, setGeolocationForBreakdown] = useState({});
  const [geolocationForRecovery, setGeolocationForRecovery] = useState({});
  const [showMapForBreakdown, setShowMapForBreakdown] = useState(false);
  const [showMapForRecovery, setShowMapForRecovery] = useState(false);
  const [taskTypes, setTaskTypes] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [showCompletionOptions, setShowCompletionOptions] = useState(false);
  const [showGarageOptions, setShowGarageOptions] = useState(false);
  const { completionCodes: completionCodes, taskType: ttypes, taskStatuses: tstatuses, vehicleColours: vehicleColors, faultType: faultTypes } = useSelector((state) => state.constants.data)
  const [showRecDestination, setShowRecDestination] = useState(false);
  const emptyEntity = useMemo(() => ({
    completionCode: '',
    completionPartsSummary: '',
    completionNotes: '',

    assignedPatrolId: 0,
    assignedPatrolName: 'ZERO',
    statusId: 6,
    typeId: 1,

    customerName: '',
    membershipInfo: '',
    contactPhoneNumber: '',
    customerAddress: '',
    customerPostcode: '',
    vehicleRegistrationNumber: '',
    caseId: "",

    location: '',
    breakdownLocation: '',
    locationDescription: '',
    recoveryDestinationInput: '',
    recoveryDestination: '',
    recoveryDestinationText: '',
    faultId: '',
    vehicleMakeId: '',
    vehicleModelId: '',
    vehicleColour: '',

    isHomestart: false,
    isPriority: false,
    requiresIntervention: false,

    comments: '',
    createdOn: '',


  }), []);
  const isEdit = Boolean(entity?.id);
  const { control, watch, handleSubmit, reset, register, setValue,trigger, formState: {/*errors*/ dirtyFields, isDirty } } = useForm({ defaultValues: emptyEntity, mode: 'onChange' });
  const currentStatus = watch('statusId');
  const currentTaskType = watch('typeId');
  const currentVehicleMake = watch('vehicleMakeId');
  const currentAssignment = watch('assignedPatrolId');
  const currentGarageAssignment = watch('assignedGarageId');
  const pickedBreakdownLocation = watch('location');
  const pickedRecoveryLocation = watch('recoveryDestination');
  const previousIsDirty = useRef(isDirty);

  useEffect(() => {
    if (entity) {
      reset(entity); // edit mode
    } else {
      reset(emptyEntity); // add mode
    }
  }, [entity, reset, emptyEntity]);

  useEffect(() => {
    if(!currentVehicleMake) {
      setValue('vehicleModelId', null)
    }
  }, [currentVehicleMake]);

  useEffect(() => {
    if (entity) {

      //breakdown
      let locationString;
      if (pickedBreakdownLocation.lat) {
        locationString = `${pickedBreakdownLocation.lat}, ${pickedBreakdownLocation.lng}`;
      } else {
        locationString = `${entity.location.lat}, ${entity.location.lng}`
      }
      setValue('breakdownLocation', locationString);

      //recovery
      let recoveryDestinationString;
      if (pickedRecoveryLocation?.lat) {
        recoveryDestinationString = `${pickedRecoveryLocation.lat}, ${pickedRecoveryLocation.lng}`;
      } else {
        if(entity.recoveryDestination && !entity.recoveryDestination?.lat) {
          recoveryDestinationString = '';
        } else {
          recoveryDestinationString = `${entity.recoveryDestination.lat}, ${entity.recoveryDestination.lng}`
        }
      }
      setValue('recoveryDestinationInput', recoveryDestinationString);
      setValue('createdOn', dayjs.utc(entity.createdOn).tz('Europe/London').format(datetimeFormat));
      entityRef.current = entity;
    }
  }, [entity]);

  useEffect(() => {
    if (previousIsDirty.current !== isDirty) {
      previousIsDirty.current = isDirty;
      setFormIsDirty(isDirty);
    }
  }, [isDirty]);

  useEffect(() => {
    register('assignedPatrolName');
  }, [register]);

  const setRecoveryAddress = (address) => {
    setValue('recoveryDestinationText', address);
  }

  const setBreakdownAddress = (address) => {
    setValue('locationDescription', address);
  }

  const handleReset = () => {
    reset();
    setFormIsDirty(false);
    //@TODO - make sure we are not unchecking boxes that were checked before the user changes the form. Also
    setShowCompletionOptions(false);
    setShowGarageOptions(false);
    hideGeolocationForBreakdown();
  }

  const onSubmit = async (data) => {
    let canCloseModal = true;
    if (data.breakdownLocation) {
      const [lat, lng] = data.breakdownLocation.split(',').map((s) => Number(s.trim()));
      if (!isNaN(lat) && !isNaN(lng)) {
        data.location = { lat, lng };
      }
    }

    if (isEdit) {
      if (data.assignedGarageId != '') {
        dispatch({
          type: 'tasks/assignGarage',
          payload: {
            taskId: data.id,
            garageId: data.assignedGarageId
          }
        });
      }

      if (data.statusId == 16 && (!data.assignedPatrolId || data.assignedPatrolId != entity.assignedPatrolId)) {
        dispatch(setNotification({
          severity: 'error',
          message: 'Task cannot be unassigned when status is in ARVD or assigned to another patrol.'
        }));
        return false;
      }

      // if(dirtyFields.assignedPatrolId && data.assignedPatrolId && data.assignedPatrolId != '') {

      //   try{
      //     const assignResp = await dispatch(assignPatrolAsync({
      //       id: data.id, patrolId: data.assignedPatrolId
      //     })).unwrap();

      //     if(assignResp.status == 200 || assignResp.status == 204) {
      //       dispatch(
      //         setNotification({ severity: 'success', message: 'Patrol assigned.' })
      //       );
      //     }
      //   } catch(error){
      //     canCloseModal = false;
      //     dispatch(
      //       setNotification({ severity: 'error', message: 'Patrol assignment error: ' + error.message })
      //     );
      //   }
      // }

      if (dirtyFields.statusId || (showCompletionOptions && data.statusId == 1)) {
        try {
          const resp = await dispatch(updateTaskStatusAsync({
            id: data.id,
            statusCode: taskStatuses.find(s => s.id == data.statusId).code,
            completionCode: data.completionCode,
            completionPartsSummary: data.completionPartsSummary,
            completionNotes: data.completionNotes
          })).unwrap();

          if (resp.status == 200 || resp.status == 204) {
            dispatch(
              setNotification({ severity: 'success', message: 'Task status updated.' })
            );
          }
        }
        catch (error) {
          canCloseModal = false;
          dispatch(
            setNotification({ severity: 'error', message: 'Task status change error: ' + error.message })
          );
        }
      }

      try {
        const updateResp = await dispatch(updateTaskAsync(data)).unwrap();
        if (updateResp.status == 200 || updateResp.status == 204) {
          dispatch(
            setNotification({ severity: 'success', message: 'Task details updated.' })
          );
          dispatch(
            {
              type: 'tasks/updateTask',
              payload: data,
            }
          );
          if (dirtyFields.assignedPatrolId && data.assignedPatrolId && data.assignedPatrolId != '') {
            dispatch(
              {
                type: 'patrols/assignTask',
                payload: {
                  patrolId: data.assignedPatrolId,
                  taskId: data.id
                },
              }
            );
          }
        }
      } catch (error) {
        canCloseModal = false;
        dispatch(
          setNotification({ severity: 'error', message: 'Task update error: ' + error.message })
        );
      }
    } else {

      try {
        const createResp = await dispatch(createTaskAsync(data)).unwrap();

        if (createResp.status == 200 || createResp.status == 204) {
          dispatch(
            setNotification({ severity: 'success', message: 'Task created.' })
          );
        }
      } catch (error) {
        canCloseModal = false;
        dispatch(
          setNotification({ severity: 'error', message: error.message })
        );
      }
    }
    if (canCloseModal) {
      onClose(false);
    }
  }

  const onError = (errors) => {
    console.log('ERRORS', errors);
    dispatch(
      setNotification({ severity: 'info', message: 'Please fill in all required fields.' })
    );
  }

  const lockTask = async () => {
    try {

      const resp = await httpClient.post(`/tasks/${entity.id}/lock`, {
        taskId: entity.id,
        userId: user.resource_id
      });
      if (resp.status == 200 || resp.status == 204) {
        dispatch(
          setNotification({ severity: 'success', message: 'Task locked.' })
        );
      }
      console.log('USER2', user);
      dispatch({
        type: 'tasks/lockTask',
        payload: {
          taskId: entity.id,
          userId: user.resource_id
        }
      });
    } catch (error) {
      dispatch(
        setNotification({ severity: 'error', message: error.message })
      );
    }
  }

  const unlockTask = async () => {
    try {
      const resp = await httpClient.post(`/tasks/${entity.id}/unlock`, {
        taskId: entity.id,
        userId: user.resource_id
      });
      if (resp.status == 200 || resp.status == 204) {
        dispatch(
          setNotification({ severity: 'success', message: 'Task unlocked.' })
        );
      }

      dispatch({
        type: 'tasks/unlockTask',
        payload: {
          taskId: entity.id
        }
      });

      // dispatch({
      //   type: 'audit/setAudit', 
      //   payload: []
      // });

    } catch (error) {
      dispatch(
        setNotification({ severity: 'error', message: error.message })
      );
    }
  }

  const handleStatusChange = (e) => {
    if (e.target.value == 1) {
      setShowCompletionOptions(true);
    } else {
      setShowCompletionOptions(false);
    }
  }

  useEffect(() => {
    setTaskTypes(ttypes);
    setTaskStatuses([...tstatuses].sort((a, b) => a.code.toLowerCase().localeCompare(b.code.toLowerCase())));
    //lock/unlock task
    if (isEdit && !entity?.lockedBy) {
      lockTask();
    }

    if (entity?.statusId == 1) {
      setShowCompletionOptions(true);
    }

    return () => {
      if (entity) {
        if (shouldUnlock()) {
          unlockTask();
        }
      }
    }

  }, []);

  useEffect(() => {
    const value = geolocationForBreakdown;
    if (geolocationForBreakdown?.lat) {
      setValue('breakdownLocation', value.lat + ', ' + value.lng);
      setValue('location', { lat: value.lat, lng: value.lng });
      trigger('breakdownLocation');
    }

  }, [geolocationForBreakdown]);

  useEffect(() => {
    const value = geolocationForRecovery;
    if (geolocationForRecovery?.lat) {
      setValue('recoveryDestinationInput', value.lat + ', ' + value.lng);
      setValue('recoveryDestination', { lat: value.lat, lng: value.lng });
      trigger('recoveryDestinationInput');
    }

  }, [geolocationForRecovery]);

  const completeTask = async () => {
    try {
      await httpClient.post(`/completeTask/id/`);
    }
    catch (error) {
      console.error("Complete Task request error:", error)
    }
    setOpenComplete(false);
  }

  const handleTabChange = (event, newValue) => {
    setTabsValue(newValue);
  }

  const showGeolocationForBreakdown = () => {
    setShowMapForBreakdown(true);
  }

  const showGeolocationForRecovery = () => {
    setShowMapForRecovery(true);
  }

  const hideGeolocationForBreakdown = () => {
    setShowMapForBreakdown(false);
  }

  const hideGeolocationForRecovery = () => {
    setShowMapForRecovery(false);
  }

  const assignGarage = (garageId) => {
    setValue('assignedGarageId', garageId);
    //setAssignedGarageId(garageId);
  }

  const handleDeassignGarage = () => {
    setValue('assignedGarageId', 0);
    setShowGarageOptions(false);
    dispatch({
      type: 'tasks/deassignGarage',
      payload: {
        taskId: entity.id
      }
    });
  }

  const shouldShowLockedAlert = () => {
    let sameUser = false;
    console.log('USER', user.resource_id, entityRef.current?.lockedBy);
    if (user.resource_id == entityRef.current?.lockedBy) {
      sameUser = true;
    }
    return entityRef.current?.lockedBy != null && !sameUser;
  }

  const entityRef = useRef(entity);

  const shouldUnlock = () => {
    let sameUser = false;
    console.log('USER 2', user.resource_id, entityRef.current?.lockedBy);
    if (user.resource_id == entityRef.current?.lockedBy) {
      sameUser = true;
    }
    return sameUser;
  }

  // const assignPatrol = (taskId, patrolId) => {
  //   return {
  //     type: 'patrols/assignTask',
  //     payload: {taskId, patrolId}
  //   }
  // }

  const fieldPermissions = {
    statusId: roles.includes(CALL_HANDLER) && !roles.includes(CONTROL_TOWER) && isEdit ? false : true,
    contactPhoneNumber: roles.includes(CALL_HANDLER) && !roles.includes(CONTROL_TOWER) && isEdit ? false : true,
    breakdownLocation: roles.includes(CALL_HANDLER) && !roles.includes(CONTROL_TOWER) && isEdit ? false : true,
    recoveryDestinationInput: roles.includes(CALL_HANDLER) && !roles.includes(CONTROL_TOWER) && isEdit ? false : true,
    assignedPatrol: roles.includes(CALL_HANDLER) && !roles.includes(CONTROL_TOWER) ? false : true,
  }

  const oldStatusRef = useRef();
  const oldAssignmentRef = useRef();

  const taskId = isEdit ? ` (Id: ${entity.id})` : '';
  return (
    <form onSubmit={handleSubmit(onSubmit)} >
      <Dialog open={open} onClose={onClose} fullWidth maxWidth='xl'>
        <DialogContent>
          <Stack direction="row" spacing={2}>
            <IconButton
              onClick={onClose}
              sx={{ position: "absolute", top: 8, right: 16 }}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Tabs  sx={{width: '80%'}} value={tabsValue} onChange={handleTabChange}>
            <Tab label={`Task Details${taskId}`} key="tab1" />
            {isEdit && <Tab label="Audit Task" key="tab2" />}
            {isEdit && <Tab label="Case Summary" key="tab3" />}
          </Tabs>
          {shouldShowLockedAlert() && <Alert sx={{ mt: 1 }} severity="error">This task is also edited by another user: {entity?.lockedBy}</Alert>}
          {entity?.requiresIntervention && <Alert sx={{ mt: 1, whiteSpace: 'pre-wrap' }} severity="warning">This task requires intervention: {entity?.interventionReason}</Alert>}
          <CustomTabPanel index={0} value={tabsValue}>

            <ConfirmDialog
              open={openComplete}
              confirmAction={completeTask}
              confirmLabel="Complete Task"
              cancelLabel="Cancel"
              cancelAction={() => { setOpenComplete(false) }}
            >
              <p>Are you sure you want to complete this task?</p>
            </ConfirmDialog>
            <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
              <Box sx={{ width: { xs: '100%', md: "30%" } }}>
                <Box
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                    marginTop: 16
                  }}
                >
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 16,
                      marginTop: 16
                    }}
                  >
                    <Box>
                      {isEdit && <FormControlLabel
                        control={
                          <Checkbox
                            checked={showCompletionOptions}
                            onChange={(e) => {
                              let oldStatus;
                              if (e.target.checked) {
                                setShowCompletionOptions(true);
                                oldStatus = currentStatus;
                                setValue('statusId', 1, { shouldDirty: true });
                              } else {
                                setShowCompletionOptions(false);
                                setValue('statusId', oldStatus);
                              }
                            }}
                          />
                        }
                        label="Complete Task"
                      />}
                    </Box>
                    <Box>
                      {Boolean(isEdit && fieldPermissions.assignedPatrol) && <FormControlLabel
                        control={
                          <Checkbox
                            checked={showGarageOptions}
                            onChange={(e) => {
                              
                              if (e.target.checked) {
                                setShowGarageOptions(true);
                                oldStatusRef.current = currentStatus;
                                oldAssignmentRef.current = currentAssignment;
                                //setValue('statusId', 14, { shouldDirty: true });
                                setValue('assignedPatrolId', 0, { shouldDirty: true });
                              } else {
                                setShowGarageOptions(false);
                                setValue('statusId', oldStatusRef.current);
                                setValue('assignedPatrolId', oldAssignmentRef.current);
                                
                              }
                            }}
                          />
                        }
                        label={(entity?.assignedGarageId || currentGarageAssignment) ? "See Garage Details" : "Garage Task"}
                      />}
                    </Box>
                  </Box>

                  {showCompletionOptions && <Box
                    sx={{
                      border: 1,
                      borderColor: '#ffcc00',
                      borderRadius: 1,
                      backgroundColor: 'hsl(45, 92%, 90%)',
                      p: 2
                    }}
                  >
                    <Controller
                      name="completionCode"
                      control={control}
                      render={({ field }) => (
                        <>
                          <FormLabel sx={{ mb: '-8px' }}>Completion Reason</FormLabel>
                          <Select label="Completion Reason" sx={{ mb: 2 }} fullWidth {...field} >
                            {completionCodes.map((reason) => (
                              <MenuItem key={reason.code} value={reason.code}>
                                {reason.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </>
                      )}
                    />

                    <Controller
                      name="completionPartsSummary"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth sx={{ mb: 2 }} >
                          <FormLabel>Completed with parts</FormLabel>
                          <TextareaAutosize
                            minRows={3}
                            placeholder="Add part info here"
                            style={{
                              width: '100%',
                              padding: 8,
                              fontSize: 16,
                              borderRadius: 4
                            }}
                            {...field}
                          />
                        </FormControl>
                      )}
                    />

                    <Controller
                      name="completionNotes"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <FormLabel>Completion Notes</FormLabel>
                          <TextareaAutosize
                            minRows={3}
                            placeholder="Completion Notes"
                            style={{
                              width: '100%',
                              padding: 8,
                              fontSize: 16,
                              borderRadius: 4
                            }}
                            {...field}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>}

                  {/*Boolean(!entity?.assignedGarageId) &&*/ <Controller
                    name="assignedPatrolId"
                    control={control}
                    defaultValue="0"
                    render={({ field: { onChange, value } }) => (
                      <>
                        <FormLabel sx={{ mb: '-25px' }}>Assigned Patrol</FormLabel>
                        <LocalAutocomplete
                          readOnly={!fieldPermissions.assignedPatrol}
                          placeholder="Type a Patrol Name or Call Sign"
                          showLabel={false}
                          valueField='id'
                          list="patrols"
                          labelField={['callsign', 'fullName']}
                          name="assignedPatrolId"
                          onChange={(e) => {
                            const value = e;
                            const selectedPatrol = patrols.find(p => p.callsign === value);
                            onChange(value);
                            setValue('assignedPatrolName', selectedPatrol?.name || '');
                          }}
                          value={value}
                        // error={!!fieldState.error}
                        // helperText={fieldState.error?.message}
                        />
                      </>
                    )}
                  />}

                  {Boolean(entity?.assignedGarageId || currentGarageAssignment) &&
                    <Box>
                      <div><strong>Assigned Garage ID: {entity?.assignedGarageId || currentGarageAssignment}</strong></div>
                      { entity?.assignedGarageName && <div><strong>Assigned Garage: {entity?.assignedGarageName}</strong></div>}
                      {Boolean(entity?.eta) && <div><strong>Garage ETA: {dayjs(entity?.eta).format(datetimeFormat)}</strong></div>}
                      {Boolean(isEdit && fieldPermissions.assignedPatrol) && <Button onClick={handleDeassignGarage} sx={{ ml: 2, mb: 1, mt: 1, background: 'var(--template-palette-action-selected)' }}>Remove</Button>}
                    </Box>
                  }

                  <Controller
                    name="typeId"
                    control={control}
                    render={({ field }) => (
                      <>
                        <FormLabel sx={{ mb: '-8px' }}>Task Type</FormLabel>
                        <Select
                          //disabled={!fieldPermissions.typeId}
                          label="Task Type"
                          fullWidth
                          {...field}
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            field.onChange(selectedValue);

                            if (selectedValue == 2) {
                              setShowRecDestination(true);
                            } else {
                              setShowRecDestination(false);
                            }
                          }}
                        >
                          {taskTypes?.map(type => <MenuItem key={type.id} value={type.id}>{type.code}</MenuItem>)}
                        </Select>
                      </>
                    )}
                  />

                  <Controller
                    name="statusId"
                    control={control}
                    render={({ field }) => (
                      <>
                        <FormLabel sx={{ mb: '-8px' }}>Task Status</FormLabel>
                        <Select
                          {...field}
                          onChange={(e) => {
                            handleStatusChange(e);
                            field.onChange(e.target.value);
                          }}
                          inputProps={{ readOnly: !fieldPermissions.statusId }}
                          label="Task Status"
                          fullWidth
                        >
                          {taskStatuses?.map(type => <MenuItem key={type.id} value={type.id}>{type.code}</MenuItem>)}
                        </Select>
                      </>
                    )}
                  />

                  <Divider sx={{ borderColor: '#ffcc00', mt: 2, mb: 2 }} />

                  <Controller
                    name="customerName"
                    control={control}
                    rules={{
                      required: "Customer Name is required",
                      maxLength: { value: 50, message: 'Customer name must have 50 characters or less.' }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        sx={{ mb: 1.5, mt: 1, '& .MuiInputLabel-asterisk': { color: 'red' } }}
                        label="Customer Name"
                        fullWidth
                        required
                        {...field}
                        onBlur={(e) => {
                          field.onChange(e.target.value.trim());
                          field.onBlur();
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />

                  <Controller
                    name="membershipInfo"
                    control={control}
                    rules={{
                      required: "Membership Info is required",
                      maxLength: {
                        value: 40,
                        message: 'Membership Info must be at most 40 characters'
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField label="Membership Information"
                        fullWidth
                        required
                        {...field}
                        onBlur={(e) => {
                          field.onChange(e.target.value.trim());
                          field.onBlur();
                        }}
                        sx={{ '& .MuiInputLabel-asterisk': { color: 'red' }, mb: 2.5, fontSize: '0.55rem' }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      // sx={{ mb: 2.5, fontSize: '0.55rem' }}
                      />
                    )}
                  />

                  <Controller
                    name="contactPhoneNumber"
                    //@TOOD fix disabled vs undefined

                    control={control}
                    rules={{
                      required: "Customer Phone Number is required",
                      maxLength: {
                        value: 16,
                        message: 'Customer Phone Number must be at most 16 characters'
                      }
                    }}
                    render={({ field, fieldState }) => {
                      return <Box sx={{ mb: 1 }}><MuiTelInput
                        defaultCountry="GB"
                        forceCallingCode
                        value={normalizePhone(field.value)}
                        onChange={field.onChange}
                        label={<>Phone Number <span style={{ color: 'red' }}>*</span></>}
                        fullWidth
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        required
                        inputProps={{ readOnly: !fieldPermissions.contactPhoneNumber }}
                      />
                      </Box>
                    }}
                  />

                  <Controller
                    name="customerAddress"
                    control={control}
                    rules={{
                      required: "Customer Address is required",
                      maxLength: {
                        value: 240,
                        message: 'Customer Address must be at most 240 characters'
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField label="Customer Address" fullWidth required {...field}
                        onBlur={(e) => {
                          field.onChange(e.target.value.trim());
                          field.onBlur();
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={{ mt: 1, '& .MuiInputLabel-asterisk': { color: 'red' } }}
                      />
                    )}
                  />

                  <Controller
                    name="customerPostcode"
                    control={control}
                    rules={{
                      required: "Customer Postcode is required",
                      maxLength: {
                        value: 8,
                        message: 'Postcode must be at most 8 characters'
                      }
                      // pattern: {
                      //   value: /^[0-9]{4,6}$/,
                      //   message: "Postcode must be 4 to 6 digits"
                      // }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField label="Customer Postcode" fullWidth required {...field}
                        onBlur={(e) => {
                          field.onChange(e.target.value.trim());
                          field.onBlur();
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        sx={{ mt: 2, '& .MuiInputLabel-asterisk': { color: 'red' } }}
                      />
                    )}
                  />

                  <Controller
                    name="caseId"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                      <LocalAutocomplete
                        required={false}
                        placeholder="Case ID - Type customer name"
                        showLabel={true}
                        list="tasks"
                        labelField="customerName"
                        name="caseId"
                        onChange={onChange}
                        value={value}
                        ref={ref}
                        // error={!!fieldState.error}
                        // helperText={fieldState.error?.message}
                      />
                    )}
                  />

                  <Divider sx={{ borderColor: '#ffcc00', mt: 2, mb: 2 }} />

                  <Controller
                    name="faultId"
                    control={control}
                    rules={{
                      required: "Fault is required"
                    }}
                    render={({ field, fieldState }) => (
                      <>
                        <FormLabel required sx={{ mb: '-8px', '& .MuiFormLabel-asterisk': { color: 'red' } }}>Fault Type</FormLabel>
                        <Select
                          label="Fault Type"
                          fullWidth
                          {...field}
                          value={field.value || ""}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        >
                          <MenuItem value="">
                            <em>Select Type</em>
                          </MenuItem>
                          {faultTypes.map((fault) => (
                            <MenuItem key={fault.key} value={fault.id}>
                              {fault.code}
                            </MenuItem>
                          ))}
                        </Select>
                        <Typography sx={{ fontSize: '0.75rem', color: '#c20a0a', mt: '-15px' }}>{fieldState.error?.message}</Typography>
                      </>
                    )}
                  />

                  <Controller
                    name="vehicleRegistrationNumber"
                    control={control}
                    rules={{
                      required: "Customer Registration Number is required",
                      maxLength: {
                        value: 11,
                        message: 'Registration Number must be at most 11 characters'
                      }
                    }}
                    render={({ field, fieldState }) => (
                      <TextField sx={{ mt: 3, '& .MuiInputLabel-asterisk': { color: 'red' } }}
                        label="Registration Number"
                        fullWidth
                        required
                        {...field}
                        onBlur={(e) => {
                          field.onChange(e.target.value.trim());
                          field.onBlur();
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />

                  <Controller
                    name="vehicleMakeId"
                    control={control}
                    rules={{
                      required: "Vehicle Brand is required"
                    }}
                    render={({ fieldState, field: { onChange, value, ref } }) => (
                      <LocalAutocomplete
                        required={true}
                        placeholder="Vehicle Brand"
                        showLabel={true}
                        list="vehicleMakes"
                        labelField="name"
                        name="vehicleMakeId"
                        onChange={onChange}
                        value={value}
                        ref={ref}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />

                    )}
                  />

                  <Controller
                    name="vehicleModelId"
                    control={control}
                    rules={{
                      required: "Vehicle Model is required"
                    }}
                    render={({ fieldState, field: { onChange, value, ref } }) => (
                      <LocalAutocomplete
                        required={true}
                        placeholder="Vehicle Model"
                        showLabel={true}
                        list="vehicleModels"
                        labelField="name"
                        name="vehicleModelId"
                        onChange={onChange}
                        value={value}
                        ref={ref}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        vehicleMakeId={currentVehicleMake}
                      />

                    )}
                  />

                  <Controller
                    name="vehicleColour"
                    control={control}
                    rules={{
                      required: "Color is required",
                      maxLength: {
                        value: 10,
                        message: 'Vehicle Color must be at most 10 characters'
                      }
                    }}
                    render={({ field, fieldState }) => {
                      return (
                        <>
                          <FormLabel sx={{ mb: '-8px', '& .MuiFormLabel-asterisk': { color: 'red' } }} required>Vehicle Color</FormLabel>
                          <Select label="Vehicle Colour"
                            fullWidth
                            {...field}
                            value={(field?.value || "").toUpperCase()}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                          >
                            {vehicleColors.map((color) => (
                              <MenuItem key={color.id} value={color.name}>
                                {color.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <Typography sx={{ fontSize: '0.75rem', color: '#c20a0a' }}>{fieldState.error?.message}</Typography>
                        </>
                      )
                    }}
                  />


                  <Controller
                    name="breakdownLocation"
                    control={control}
                    rules={{
                      required: "Breakdown Location is required",
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        sx={{ mt: 1, '& .MuiInputLabel-asterisk': { color: 'red' } }}
                        onFocus={() => {
                          //if (fieldPermissions.breakdownLocation) {
                            showGeolocationForBreakdown();
                          //}
                        }}
                        label="Breakdown Location"
                        fullWidth
                        required
                        {...field}
                        onBlur={(e) => {
                          field.onChange(e.target.value.trim());
                          field.onBlur();
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ 
                          readOnly: !fieldPermissions.breakdownLocation ,
                        }}
                      />
                    )}
                  />

                  <Controller
                    name="locationDescription"
                    control={control}
                    rules={{
                      required: "Breakdown Details is required",
                      //validate: value => value.trim() !== '' || 'Breakdown Details is required' ,
                      maxLength: {
                        value: 250,
                        message: 'Breakdown Details must be at most 250 characters'
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        label="Breakdown Details"
                        fullWidth
                        sx={{ mt: 2, '& .MuiInputLabel-asterisk': { color: 'red' } }}
                        required
                        {...field}
                        onBlur={(e) => {
                          field.onChange(e.target.value.trim());
                          field.onBlur();
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />

                  
{Boolean(showRecDestination || entity?.typeId == 2) && <>
                    <Controller
                      name="recoveryDestinationInput"
                      control={control}
                      rules={
                        currentTaskType == 2 ? {required: "Recovery Destination is required",} : undefined
                      }
                      render={({ field, fieldState }) => (
                        <TextField 
                          inputProps={{ readOnly: !fieldPermissions.recoveryDestinationInput }}
                          onFocus={() => {
                            //if (fieldPermissions.recoveryDestinationInput) {
                              showGeolocationForRecovery();
                            //}
                          }}
                          sx={{ mt: 1.5, '& .MuiInputLabel-asterisk': { color: 'red' } }}
                          required={currentTaskType == 2 ? true : false}
                          label="Recovery Destination"
                          fullWidth
                          {...field}
                          onBlur={(e) => {
                            field.onChange(e.target.value.trim());
                            field.onBlur();
                          }}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />

                    <Controller
                      name="recoveryDestinationText"
                      control={control}
                      rules={
                        currentTaskType == 2 ? {required: "Recovery Destination Description is required",} : undefined
                      }
                      render={({ field, fieldState }) => (
                        <TextField 
                          label="Recovery Destination Description" 
                          fullWidth 
                          {...field} 
                          onBlur={(e) => {
                            field.onChange(e.target.value.trim());
                            field.onBlur();
                          }}
                          sx={{ mt: 1.5, '& .MuiInputLabel-asterisk': { color: 'red' } }}
                          required={currentTaskType == 2 ? true : false}
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message}
                        />
                      )}
                    />
                  </>}

                  <Divider sx={{ borderColor: '#ffcc00', mt: 0.5, mb: 1.5 }} />

                  <Controller
                    name="isHomestart"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={Boolean(Number(field.value))}
                          />
                        }
                        label="Home Start"
                      />
                    )}
                  />

                  <Controller
                    name="isPriority"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={Boolean(Number(field.value))}
                          />
                        }
                        label="Priority"
                      />
                    )}
                  />

                  <Controller
                    name="requiresIntervention"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={Boolean(Number(field.value))}
                          />
                        }
                        label="Requires Intervention"
                      />
                    )}
                  />

                  <Divider sx={{ borderColor: '#ffcc00', mt: 0.5, mb: 1 }} />

                  <Controller
                    name="comments"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <FormLabel>Comments</FormLabel>
                        <TextareaAutosize
                          minRows={3}
                          placeholder="Comments"
                          style={{
                            width: '100%',
                            padding: 8,
                            fontSize: 16,
                            borderRadius: 4
                          }}
                          {...field}
                        />
                      </FormControl>
                    )}
                  />

                  {isEdit && <Controller
                    name="createdOn"
                    control={control}
                    render={({ field }) => (
                      <TextField disabled label="Creation Date/Time" sx={{ mt: 2, }} fullWidth {...field} />
                    )}
                  />}

                </Box>
              </Box>

              {Boolean(showGarageOptions /*|| entity?.assignedGarageId*/) &&
                <Box sx={{
                  width: "70%",

                }}>
                  <GarageTaskGrid assignGarage={assignGarage} task={entity} />
                </Box>
              }

              {Boolean(showMapForBreakdown || showMapForRecovery) && <Box sx={{
                width: "70%",
                display: "flex",
                flexDirection: "column",
                minHeight: 600
              }}>
                {showMapForBreakdown &&
                  <Geolocation
                    location={function () {
                      if (pickedBreakdownLocation) {
                        return pickedBreakdownLocation;
                      } else {
                        return entity?.location;
                      }
                    }()}
                    hideGeolocation={hideGeolocationForBreakdown}
                    setGeolocation={setGeolocationForBreakdown}
                    setAddress={setBreakdownAddress}
                  />
                }
                {showMapForRecovery &&
                  <Geolocation
                    location={function () {
                      if (pickedRecoveryLocation) {
                        return pickedRecoveryLocation;
                      } else {
                        return entity?.recoveryDestination;
                      }
                    }()}
                    hideGeolocation={hideGeolocationForRecovery}
                    setGeolocation={setGeolocationForRecovery}
                    setAddress={setRecoveryAddress}
                  />}
              </Box>}
            </Box>
          </CustomTabPanel>
          {isEdit &&
            <CustomTabPanel value={tabsValue} index={1}>
              <Typography
                component="h1"
                variant="subtitle1"
                sx={{

                  pr: 4
                }}
              >Task Audit Entries
              </Typography>

              {entity && <AuditGrid entityId={entity.id} />}
            </CustomTabPanel>
          }
          {isEdit &&
            <CustomTabPanel value={tabsValue} index={2}>
              <Typography
                component="h1"
                variant="subtitle1"
                sx={{

                  pr: 4
                }}
              >Case Tasks Summary
              </Typography>

              <CaseGrid caseId={entity.id} statuses={taskStatuses} taskTypes={taskTypes} />
            </CustomTabPanel>
          }
        </DialogContent>

        {tabsValue == 0 && <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleReset}>Reset</Button>
          <Button onClick={handleSubmit(onSubmit, onError)} type="submit" variant="contained" color="primary" loading={taskLoading} disabled={taskLoading}>
            {isEdit ? "Resubmit" : "Add new task"}
          </Button>
        </DialogActions>}
      </Dialog>
    </form>
  );
};

export default TaskForm;
